// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

/**
 * Checks if two arrays contain the same elements in the same order
 */
const arraysInSameOrder = <T>(first: T[], second: T[]): boolean => {
    if (first.length !== second.length) {
        return false;
    }

    for (let i = 0; i < first.length; i++) {
        if (first[i] !== second[i]) {
            return false;
        }
    }

    return true;
};

/**
 * Shuffle an array with the Fisher-Yates shuffle algorithm
 */
export const shuffle = <T>(array: T[]): T[] => {
    /* Handle empty/single-element array case */
    if (array.length <= 1) {
        return [...array];
    }

    /* Handle case when all elements are identical */
    if (new Set(array).size === 1) {
        return [...array];
    }

    const original = [...array];
    let shuffledArray: T[];

    /* Keep shuffling until we get a different order */
    do {
        shuffledArray = [...array];
        let remaining = array.length;

        /* Fisher-Yates shuffle with unbiased random index generation */
        while (remaining) {
            const currentIndex = remaining - 1;
            const randomValue = new Uint32Array(1);
            crypto.getRandomValues(randomValue);
            const randomIndex = Math.floor((randomValue[0] / (2 ** 32)) * remaining);

            [
                shuffledArray[currentIndex],
                shuffledArray[randomIndex],
            ] = [
                shuffledArray[randomIndex],
                shuffledArray[currentIndex],
            ];
            remaining--;
        }
    } while (arraysInSameOrder(original, shuffledArray));

    return shuffledArray;
};
